import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getConsent } from '../components/CookieBanner';

const STORAGE_KEY = 'zog_cookie_consent_v1';

const Row = ({ name, provider, purpose, expiry, type }) => (
  <tr className="border-b border-border">
    <td className="py-3 pr-4 align-top font-medium text-sm">{name}</td>
    <td className="py-3 pr-4 align-top text-sm text-neutral-600">{provider}</td>
    <td className="py-3 pr-4 align-top text-sm text-neutral-600">{purpose}</td>
    <td className="py-3 pr-4 align-top text-sm text-neutral-600">{expiry}</td>
    <td className="py-3 align-top text-[10px] uppercase tracking-[0.15em] text-neutral-500">{type}</td>
  </tr>
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-serif text-2xl sm:text-3xl mb-4 leading-tight">{title}</h2>
    <div className="space-y-4 text-sm sm:text-[15px] leading-relaxed text-neutral-700">
      {children}
    </div>
  </section>
);

const CookiePolicyPage = () => {
  const [consent, setConsent] = useState(getConsent());

  useEffect(() => {
    const onChange = () => setConsent(getConsent());
    window.addEventListener('zog:consent-change', onChange);
    return () => window.removeEventListener('zog:consent-change', onChange);
  }, []);

  const resetConsent = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('zog:consent-change', { detail: null }));
      window.location.reload();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('[cookies] reset failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Cookie Policy — Zebra One Gallery"
        description="Details of the cookies used on zebraonegallery.com and how to manage them under UK GDPR."
        canonical="https://zebraonegallery.com/cookies"
      />
      <Header />

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="cookie-policy-page">
        <div className="mb-12 border-b border-border pb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">Legal</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Cookie Policy
          </h1>
          <p className="text-xs text-neutral-500 mt-4">Last updated: May 2026</p>
        </div>

        <Section title="What are cookies?">
          <p>
            Cookies are small text files stored on your device when you visit a
            website. They help the site remember you, your preferences, and how
            you use it. We use a small number of cookies — listed below — and
            ask for your consent for everything that isn't strictly essential.
          </p>
        </Section>

        <Section title="Your current preferences">
          {consent ? (
            <div className="border border-border p-5 bg-neutral-50">
              <ul className="text-sm space-y-1">
                <li>Essential: <strong>Always on</strong></li>
                <li>Analytics: <strong>{consent.analytics ? 'Allowed' : 'Disabled'}</strong></li>
                <li>Marketing: <strong>{consent.marketing ? 'Allowed' : 'Disabled'}</strong></li>
              </ul>
              <p className="text-xs text-neutral-500 mt-3">
                Set on {new Date(consent.decidedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
              </p>
              <button
                onClick={resetConsent}
                className="mt-4 px-4 py-2 border border-black text-black text-[11px] uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors"
                data-testid="cookie-reset-preferences"
              >
                Change my preferences
              </button>
            </div>
          ) : (
            <p>You haven't set your cookie preferences yet. The banner will appear shortly.</p>
          )}
        </Section>

        <Section title="Cookies we use">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.15em]">Name</th>
                  <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.15em]">Provider</th>
                  <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.15em]">Purpose</th>
                  <th className="py-3 pr-4 text-[10px] uppercase tracking-[0.15em]">Expiry</th>
                  <th className="py-3 text-[10px] uppercase tracking-[0.15em]">Type</th>
                </tr>
              </thead>
              <tbody>
                <Row name="zog_cookie_consent_v1" provider="Zebra One Gallery" purpose="Remembers your cookie choices" expiry="12 months" type="Essential" />
                <Row name="zog_auth_token" provider="Zebra One Gallery" purpose="Keeps gallery staff logged in to the admin CMS" expiry="Session" type="Essential" />
                <Row name="zog_geo" provider="Zebra One Gallery" purpose="Caches detected country for VAT display" expiry="24 hours" type="Essential" />
                <Row name="__stripe_*" provider="Stripe" purpose="Fraud prevention during checkout" expiry="1 hour to 1 year" type="Essential" />
                <Row name="_ga, _ga_*" provider="Google Analytics 4" purpose="Anonymous usage statistics (page views, devices)" expiry="2 years" type="Analytics" />
                <Row name="MC*" provider="Mailchimp" purpose="Tracks newsletter signups" expiry="13 months" type="Marketing" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Managing cookies">
          <p>
            You can change your choice at any time using the button above, or by
            clearing cookies in your browser settings. Disabling essential
            cookies may break parts of the site (checkout, admin login).
          </p>
          <p>
            For more on cookies see <a href="https://ico.org.uk/your-data-matters/online/cookies/" target="_blank" rel="noopener noreferrer" className="underline">ICO guidance on cookies</a>.
          </p>
        </Section>

        <Section title="Related">
          <p>
            See our <Link to="/privacy" className="underline">privacy policy</Link> for details on how we handle personal data overall.
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
